import type { FilterPreset } from '../../shared/hooks/useFilterPresets';

/**
 * Pre-defined filter presets for the Audit Logs page.
 *
 * Each preset maps to a named {@link FilterPreset} that the user can apply
 * with a single click via the preset UI (P1/P2/P3 variants).
 *
 * Field keys and selection values match the `AUDIT_LOGS_FIELDS_REGISTRY`
 * in `domains/audit/fields.ts`.
 */
export const AUDIT_PRESETS: FilterPreset[] = [
  {
    id: 'audit_manufacturers',
    label: 'Manufacturers',
    description: 'Changes to the Manufacturers table',
    query: {
      groupOperator: 'AND',
      filterGroups: [
        {
          id: 'audit_mfr_grp',
          logicalOperator: 'AND',
          rules: [
            {
              id: 'audit_mfr_rule',
              field: 'table_name',
              operator: 'equals',
              value: 'Manufacturers',
            },
          ],
        },
      ],
    },
  },
  {
    id: 'audit_products',
    label: 'Products',
    description: 'Changes to the Products table',
    query: {
      groupOperator: 'AND',
      filterGroups: [
        {
          id: 'audit_products_grp',
          logicalOperator: 'AND',
          rules: [
            {
              id: 'audit_products_rule',
              field: 'table_name',
              operator: 'equals',
              value: 'Products',
            },
          ],
        },
      ],
    },
  },
  {
    id: 'audit_facilities',
    label: 'Facilities',
    description: 'Changes to the Facilities table',
    query: {
      groupOperator: 'AND',
      filterGroups: [
        {
          id: 'audit_facilities_grp',
          logicalOperator: 'AND',
          rules: [
            {
              id: 'audit_facilities_rule',
              field: 'table_name',
              operator: 'equals',
              value: 'Facilities',
            },
          ],
        },
      ],
    },
  },
  {
    id: 'audit_deletes',
    label: 'Deletes',
    description: 'All DELETE operations across all tables',
    query: {
      groupOperator: 'AND',
      filterGroups: [
        {
          id: 'audit_deletes_grp',
          logicalOperator: 'AND',
          rules: [
            {
              id: 'audit_deletes_rule',
              field: 'operation_type',
              operator: 'equals',
              value: 'DELETE',
            },
          ],
        },
      ],
    },
  },
  {
    id: 'audit_merges',
    label: 'Merges',
    description: 'All MERGE operations (record consolidation)',
    query: {
      groupOperator: 'AND',
      filterGroups: [
        {
          id: 'audit_merges_grp',
          logicalOperator: 'AND',
          rules: [
            {
              id: 'audit_merges_rule',
              field: 'operation_type',
              operator: 'equals',
              value: 'MERGE',
            },
          ],
        },
      ],
    },
  },
  {
    id: 'audit_product_types',
    label: 'Product Types',
    description: 'Changes to the Grades (Product Types) table',
    query: {
      groupOperator: 'AND',
      filterGroups: [
        {
          id: 'audit_product_types_grp',
          logicalOperator: 'AND',
          rules: [
            {
              id: 'audit_product_types_rule',
              field: 'table_name',
              operator: 'equals',
              value: 'Grades',
            },
          ],
        },
      ],
    },
  },
  {
    id: 'audit_therapeutic_areas',
    label: 'Therapeutic Areas',
    description: 'Changes to the Therapeutic Areas table',
    query: {
      groupOperator: 'AND',
      filterGroups: [
        {
          id: 'audit_therapeutic_areas_grp',
          logicalOperator: 'AND',
          rules: [
            {
              id: 'audit_therapeutic_areas_rule',
              field: 'table_name',
              operator: 'equals',
              value: 'therapeutic_areas',
            },
          ],
        },
      ],
    },
  },
];
